import { useState, useEffect, useRef } from "react";
import "./akyo-qcm.css";
export type Fragment = {
  text?: string;
  media?: { type: string; src: string; alt?: string }[];
};
type Element = { id: string; content: Fragment };
export type NativeQuestion = {
  instanceId: string;
  questionType: string;
  prompt: Fragment;
  body: {
    choices?: Element[];
    items?: Element[];
    left?: Element[];
    right?: Element[];
    statements?: Element[];
    holes?: { id: string; choices: Element[] }[];
    segments?: (string | { holeId: string })[];
  };
};
export function AkyoFragment({ value }: { value: Fragment }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const render = () => {
      if (ref.current)
        void (window as any).MathJax?.typesetPromise?.([ref.current]).catch(
          () => {},
        );
    };
    if ((window as any).MathJax?.typesetPromise) {
      render();
      return;
    }
    let script = document.getElementById(
      "competenceo-mathjax",
    ) as HTMLScriptElement | null;
    if (!script) {
      (window as any).MathJax = {
        tex: {
          inlineMath: [
            ["$", "$"],
            ["\\(", "\\)"],
          ],
          displayMath: [
            ["$$", "$$"],
            ["\\[", "\\]"],
          ],
        },
        svg: { fontCache: "global" },
      };
      script = document.createElement("script");
      script.id = "competenceo-mathjax";
      script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js";
      script.async = true;
      document.head.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => script?.removeEventListener("load", render);
  }, [value.text]);
  return (
    <span ref={ref}>
      {value.text}
      {value.media?.map((m, i) => (
        <img
          key={i}
          src={m.src}
          alt={m.alt || "Document scientifique"}
          style={{ maxWidth: "100%", maxHeight: 300 }}
        />
      ))}
    </span>
  );
}
export default function AkyoQuestion({
  question: q,
  onAnswer,
  disabled = false,
  correction,
  response,
}: {
  question: NativeQuestion;
  onAnswer?: (answer: unknown) => Promise<void>;
  disabled?: boolean;
  response?: any;
  correction?: { answer: Record<string, any>; explanation: Fragment } | null;
}) {
  const [selected, setSelected] = useState<Record<string, any>>(() =>
    response
      ? {
          choice: response.choiceId,
          ...Object.fromEntries(
            (response.choiceIds || []).map((id: string) => [id, true]),
          ),
          ...Object.fromEntries(
            (response.holes || response.pairs || response.statements || []).map(
              (row: any) => [
                row.holeId || row.leftId || row.statementId,
                row.choiceId ?? row.rightId ?? row.value,
              ],
            ),
          ),
        }
      : {},
  );
  const [order, setOrder] = useState<string[]>(
    response?.itemIds || q.body.items?.map((i) => i.id) || [],
  );
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const b = q.body;
  const set = (id: string, value: unknown) => {
    setSelected((v) => ({ ...v, [id]: value }));
    setSent(false);
  };
  const choice = (items: Element[], name: string, multiple = false) =>
    items.map((i, index) => (
      <label key={i.id} data-choice-id={i.id} className={`akyo-choice${correction && (multiple ? correction.answer.choiceIds?.includes(i.id) : correction.answer.choiceId === i.id) ? " akyo-choice-correct" : ""}`} style={{ display: "block", padding: 10 }}>
        <input
          disabled={disabled || !onAnswer}
          type={multiple ? "checkbox" : "radio"}
          name={onAnswer ? name : `${q.instanceId}:${name}`}
          checked={multiple ? !!selected[i.id] : selected[name] === i.id}
          onChange={(e) =>
            set(multiple ? i.id : name, multiple ? e.target.checked : i.id)
          }
        />{" "}
        <span className="akyo-choice-letter" aria-hidden="true">{String.fromCharCode(65 + index)}</span>{" "}
        <AkyoFragment value={i.content} />
        {correction && (multiple ? correction.answer.choiceIds?.includes(i.id) : correction.answer.choiceId === i.id) && <strong className="akyo-choice-feedback"> ✓ Bonne réponse</strong>}
      </label>
    ));
  let answer: unknown,
    complete = true;
  if (q.questionType === "single_choice") {
    answer = { choiceId: selected.choice };
    complete = !!selected.choice;
  } else if (q.questionType === "multiple_choice")
    answer = {
      choiceIds: b.choices!.filter((i) => selected[i.id]).map((i) => i.id),
    };
  else if (q.questionType === "ordering") answer = { itemIds: order };
  else if (q.questionType === "matching") {
    const pairs = b.left!.map((i) => ({
      leftId: i.id,
      rightId: selected[i.id],
    }));
    answer = { pairs };
    complete =
      pairs.every((i) => !!i.rightId) &&
      new Set(pairs.map((i) => i.rightId)).size === pairs.length;
  } else if (q.questionType === "true_false") {
    const statements = b.statements!.map((i) => ({
      statementId: i.id,
      value: selected[i.id],
    }));
    answer = { statements };
    complete = statements.every((i) => typeof i.value === "boolean");
  } else if (q.questionType === "cloze_dropdown") {
    const holes = b.holes!.map((i) => ({
      holeId: i.id,
      choiceId: selected[i.id],
    }));
    answer = { holes };
    complete = holes.every((i) => !!i.choiceId);
  } else
    return (
      <p role="alert">Type Akyo non pris en charge : lancement impossible.</p>
    );
  const textFor = (id: string) =>
    [
      ...(b.choices || []),
      ...(b.items || []),
      ...(b.left || []),
      ...(b.right || []),
      ...(b.statements || []),
      ...(b.holes || []).flatMap((h) => h.choices),
    ].find((i) => i.id === id)?.content.text || id;
  return (
    <div
      className="akyo-question"
      data-instance={q.instanceId}
      style={{
        padding: 16,
        background: "white",
        color: "#18263b",
        borderRadius: 12,
      }}
    >
      <p>
        <AkyoFragment value={q.prompt} />
      </p>
      {(q.questionType === "single_choice" ||
        q.questionType === "multiple_choice") &&
        choice(b.choices!, "choice", q.questionType === "multiple_choice")}
      {q.questionType === "ordering" && (
        <ol>
          {order.map((id, index) => (
            <li key={id}>
              <AkyoFragment
                value={b.items!.find((i) => i.id === id)!.content}
              />
              {onAnswer && (
                <>
                  <button
                    disabled={disabled || index === 0}
                    aria-label={`Monter ${index + 1}`}
                    onClick={() =>
                      setOrder((ids) => {
                        const next = [...ids];
                        [next[index - 1], next[index]] = [
                          next[index],
                          next[index - 1],
                        ];
                        return next;
                      })
                    }
                  >
                    ↑
                  </button>
                  <button
                    disabled={disabled || index === order.length - 1}
                    aria-label={`Descendre ${index + 1}`}
                    onClick={() =>
                      setOrder((ids) => {
                        const next = [...ids];
                        [next[index + 1], next[index]] = [
                          next[index],
                          next[index + 1],
                        ];
                        return next;
                      })
                    }
                  >
                    ↓
                  </button>
                </>
              )}
            </li>
          ))}
        </ol>
      )}
      {q.questionType === "true_false" &&
        b.statements!.map((i) => (
          <fieldset key={i.id}>
            <legend>
              <AkyoFragment value={i.content} />
            </legend>
            {[true, false].map((v) => (
              <label key={String(v)}>
                <input
                  type="radio"
                  disabled={disabled || !onAnswer}
                  name={i.id}
                  checked={selected[i.id] === v}
                  onChange={() => set(i.id, v)}
                />
                {v ? "Vrai" : "Faux"}
              </label>
            ))}
          </fieldset>
        ))}
      {q.questionType === "matching" &&
        b.left!.map((i) => (
          <fieldset key={i.id}>
            <legend>
              <AkyoFragment value={i.content} />
            </legend>
            {choice(b.right!, i.id)}
          </fieldset>
        ))}
      {q.questionType === "cloze_dropdown" && (
        <>
          {b.segments?.map((s, i) => (
            <span key={i}>
              {typeof s === "string"
                ? s
                : ` [${b.holes!.findIndex((h) => h.id === s.holeId) + 1}] `}
            </span>
          ))}
          {b.holes!.map((h, i) => (
            <fieldset key={h.id}>
              <legend>Trou {i + 1}</legend>
              <select
                aria-label={`Trou ${i + 1}`}
                disabled={disabled || !onAnswer}
                value={selected[h.id] || ""}
                onChange={(e) => set(h.id, e.target.value)}
              >
                <option value="">Choisir</option>
                {h.choices.map((c, n) => (
                  <option key={c.id} value={c.id}>
                    {c.content.text || `Document ${n + 1}`}
                  </option>
                ))}
              </select>
              {h.choices
                .filter((c) => c.content.media?.length)
                .map((c) => (
                  <p key={c.id}>
                    <AkyoFragment value={c.content} />
                  </p>
                ))}
            </fieldset>
          ))}
        </>
      )}
      {onAnswer && (
        <button
          disabled={disabled || !complete}
          onClick={async () => {
            setError("");
            try {
              await onAnswer(answer);
              setSent(true);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Envoi impossible");
            }
          }}
        >
          Envoyer la réponse
        </button>
      )}
      {sent && <p role="status">Réponse enregistrée</p>}
      {error && <p role="alert">{error}</p>}
      {correction && (
        <aside>
          <strong>Correction Akyo</strong>
          <p>
            {Object.entries(correction.answer)
              .filter(([k]) => k !== "grading")
              .map(([key, value]) =>
                typeof value === "string"
                  ? textFor(value)
                  : Array.isArray(value)
                    ? value
                        .map((v) =>
                          typeof v === "string"
                            ? textFor(v)
                            : Object.values(v)
                                .map((x) =>
                                  typeof x === "string"
                                    ? textFor(x)
                                    : x === true
                                      ? "Vrai"
                                      : "Faux",
                                )
                                .join(" → "),
                        )
                        .join(" ; ")
                    : String(value),
              )
              .join(" ; ")}
          </p>
          <AkyoFragment value={correction.explanation} />
        </aside>
      )}
    </div>
  );
}
